import { IconButton, Tooltip, Typography } from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import { Star } from "lucide-react";
import { Document, DocumentType } from "protogen/documents_pb";
import { useState } from "react";
import { useEffect } from "react";
import { useToggleDocumentFavorite } from "services/documents";

export const DocumentFavoriteIcon = ({
  document,
  showCount = true,
  showTooltip = true,
  onUpdate,
  size = "regular",
  isStacked = false,
  sx,
}: {
  document: Document;
  showCount: boolean;
  showTooltip?: boolean;
  onUpdate?: (starred: boolean) => void;
  size?: "small" | "regular";
  isStacked?: boolean;
  sx?: React.CSSProperties;
}) => {
  const [isFavorite, setIsFavorite] = useState(document.isFavorite);
  const [favoriteCount, setFavoriteCount] = useState(
    Number(document.favoriteCount),
  );
  const isMobile = useIsMobile();
  useEffect(() => {
    setIsFavorite(document.isFavorite);
    setFavoriteCount(Number(document.favoriteCount));
  }, [document.isFavorite, document.favoriteCount]);
  const { request, loading } = useToggleDocumentFavorite();

  const entityType = () => {
    if (document?.documentType === DocumentType.TASK_BLOCK) {
      return "task-block";
    } else if (document?.documentType === DocumentType.CALL) {
      return "phonecall-note";
    } else if (document?.documentType === DocumentType.FACT) {
      return "fact";
    } else if (
      document?.documentType === DocumentType.NOTE &&
      document?.inboxType === "family"
    ) {
      return "family";
    } else if (document?.documentType === DocumentType.NOTE) {
      return "advisor";
    } else if (document?.documentType === DocumentType.FORM) {
      return "advisor";
    }
  };

  const handleToggleFavorite = async () => {
    const resp = await request({
      documentRef:
        document?.documentType === DocumentType.TASK_BLOCK
          ? document?.taskBlock?.ref ?? ""
          : document?.ref ?? "",
      documentType: document.documentType,
      entityType: entityType() ?? "",
      isFavorite: !isFavorite,
    });
    if (resp) {
      setIsFavorite(resp.isFavorite);
      setFavoriteCount((prev) => (resp.isFavorite ? prev + 1 : prev - 1));
      onUpdate?.(resp.isFavorite);
    }
  };
  return (
    <Tooltip title={showTooltip ? document.favoriteCount || "" : ""}>
      <IconButton
        disabled={loading}
        sx={{
          flexDirection: isStacked ? "column" : "row",
          justifyContent: "flex-start",
          width: "fit-content",
          gap: isStacked ? "2px" : "4px",
          padding: 0,
          ...sx,
        }}
        aria-label="toggle favorite"
        onClick={handleToggleFavorite}
      >
        <Star
          size={
            isMobile
              ? size == "small"
                ? "20px"
                : "24px"
              : size == "small"
                ? "16px"
                : "20px"
          }
          fill={isFavorite ? "#F59F51" : "transparent"}
          stroke={isFavorite ? "#F59F51" : "#B3AFB6"}
          strokeWidth={1.5}
        />
        {showCount && favoriteCount > 0 && (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{ lineHeight: "100%" }}
          >
            {favoriteCount}
          </Typography>
        )}
      </IconButton>
    </Tooltip>
  );
};
